import React, { useState } from "react";
import { useAuth } from "../../../../Security/AuthProvider";
import "../CreateForm.css";

function FormCreation({ fetchForms, setShowPopup }) {
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [fields, setFields] = useState([]);
  const [currentField, setCurrentField] = useState({
    name: "",
    label: "",
    type: "text",
    placeholder: "",
    required: false,
    options: [],
  });
  const [currentOption, setCurrentOption] = useState({
    value: "",
    label: "",
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { getCredentials } = useAuth();

  const fieldTypes = [
    { value: "text", label: "Champ de texte" },
    { value: "checkbox", label: "Choix multiple" },
    { value: "radio", label: "Choix unique" },
    { value: "image", label: "Envoie d'image" },
  ];

  const handleAddField = () => {
    if (!currentField.name || !currentField.label) {
      alert("Le nom et le label du champ sont requis");
      return;
    }

    // Generate a safe field name if not provided
    const fieldName = currentField.name.replace(/\s+/g, "_").toLowerCase();

    // Check for duplicate field names
    if (fields.some((field) => field.name === fieldName)) {
      alert("Le nom du champ doit être unique");
      return;
    }

    setFields([...fields, { ...currentField, name: fieldName }]);
    setCurrentField({
      name: "",
      label: "",
      type: "text",
      placeholder: "",
      required: false,
      options: [],
    });
  };

  const handleAddOption = () => {
    if (!currentOption.value || !currentOption.label) {
      alert("La valeur et le label de l'option sont requis");
      return;
    }

    setCurrentField({
      ...currentField,
      options: [...currentField.options, { ...currentOption }],
    });
    setCurrentOption({ value: "", label: "" });
  };

  const handleRemoveField = (index) => {
    const updatedFields = [...fields];
    updatedFields.splice(index, 1);
    setFields(updatedFields);
  };

  const handleRemoveOption = (index) => {
    const updatedOptions = [...currentField.options];
    updatedOptions.splice(index, 1);
    setCurrentField({
      ...currentField,
      options: updatedOptions,
    });
  };

  const handleMoveField = (index, direction) => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === fields.length - 1)
    ) {
      return;
    }

    const updatedFields = [...fields];
    const swapIndex = direction === "up" ? index - 1 : index + 1;

    [updatedFields[index], updatedFields[swapIndex]] = [
      updatedFields[swapIndex],
      updatedFields[index],
    ];

    setFields(updatedFields);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!formName) {
      setError("Le nom du formulaire est requis");
      return;
    }

    if (fields.length === 0) {
      setError("Ajouter au moins une question à votre formulaire");
      return;
    }

    try {
      // Prepare fields with correct property names to match backend expectations
      const formattedFields = fields.map(field => {
        const formattedField = {
          field_type: field.type,
          field_name: field.name,
          field_label: field.label,
          field_placeholder: field.placeholder || '',
          is_required: field.required ? 1 : 0,
          // Keep options with the same structure if they exist
          options: field.options ? field.options.map(opt => ({
            option_value: opt.value,
            option_label: opt.label
          })) : []
        };
        return formattedField;
      });

      setLoading(true);
      setError(null);
      
      console.log("Création du formulaire avec les données:", {
        name: formName,
        description: formDescription,
        fields: formattedFields
      });

      const response = await fetch("/forms/create", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formName,
          description: formDescription,
          fields: formattedFields,
          company_name: getCredentials().company_name,
          company_password: getCredentials().company_password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Erreur lors de la création:", errorData);
        
        // Gestion des erreurs spécifiques
        if (errorData.error?.includes("existe déjà")) {
          throw new Error("Un formulaire avec ce nom existe déjà. Veuillez choisir un autre nom.");
        } else if (errorData.error?.includes("timeout") || errorData.error?.includes("lock")) {
          throw new Error("Le serveur est temporairement occupé. Veuillez réessayer dans quelques secondes.");
        } else {
          throw new Error(errorData.error || "Échec de la création du formulaire");
        }
      }

      const result = await response.json();
      console.log("Formulaire créé avec succès:", result);

      // Form created successfully
      await fetchForms();
      setShowPopup(false);
      
      // Afficher un message de succès
      alert("✅ Formulaire créé avec succès!");
      
    } catch (err) {
      console.error("Erreur lors de la création du formulaire:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const needsOptions = ["radio", "checkbox", "select"].includes(
    currentField.type
  );

  return (
    <div className="form-creation-popup" onClick={(e) => e.stopPropagation()}>
      <h2>Créer un nouveau formulaire</h2>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-section">
          <h3>Détails du formulaire</h3>
          <div className="form-group">
            <label>Nom du formulaire *</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="Entrez le nom du formulaire"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="Entrez la description du formulaire (optionnel)"
              rows="3"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Champs du formulaire</h3>

          {fields.length > 0 && (
            <div className="fields-list">
              {fields.map((field, index) => (
                <div key={index} className="field-item">
                  <div className="field-header">
                    <h4>{field.label}</h4>
                    <div className="field-actions">
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => handleMoveField(index, "up")}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="move-btn"
                        onClick={() => handleMoveField(index, "down")}
                        disabled={index === fields.length - 1}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => handleRemoveField(index)}
                      >
                        Supprimer
                      </button>
                    </div>
                  </div>

                  <div className="field-details">
                    <p>
                      <strong>Type:</strong>{" "}
                      {fieldTypes.find((t) => t.value === field.type)?.label ||
                        field.type}
                    </p>
                    <p>
                      <strong>Nom:</strong> {field.name}
                    </p>
                    {field.placeholder && (
                      <p>
                        <strong>Placeholder:</strong> {field.placeholder}
                      </p>
                    )}
                    <p>
                      <strong>Obligatoire:</strong>{" "}
                      {field.required ? "Oui" : "Non"}
                    </p>

                    {field.options && field.options.length > 0 && (
                      <div className="field-options">
                        <p>
                          <strong>Options:</strong>
                        </p>
                        <ul>
                          {field.options.map((option, optIdx) => (
                            <li key={optIdx}>
                              {option.label} ({option.value})
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="add-field-section">
            <h4>Ajouter un nouveau champ</h4>
            <div className="field-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Libellé du champ *</label>
                  <input
                    type="text"
                    value={currentField.label}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        label: e.target.value,
                      })
                    }
                    placeholder="ex: Êtes-vous arrivé à l'heure ?"
                  />
                </div>
                <div className="form-group">
                  <label>Nom de l'information *</label>
                  <input
                    type="text"
                    value={currentField.name}
                    onChange={(e) =>
                      setCurrentField({ ...currentField, name: e.target.value })
                    }
                    placeholder="ex: Retard :"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Type de champ</label>
                  <select
                    value={currentField.type}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        type: e.target.value,
                        options: ["radio", "checkbox", "select"].includes(
                          e.target.value
                        )
                          ? currentField.options
                          : [],
                      })
                    }
                  >
                    {fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Placeholder (optionnel)</label>
                  <input
                    type="text"
                    value={currentField.placeholder || ""}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        placeholder: e.target.value,
                      })
                    }
                    placeholder="ex: Oui/Non"
                  />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={currentField.required}
                    onChange={(e) =>
                      setCurrentField({
                        ...currentField,
                        required: e.target.checked,
                      })
                    }
                  />
                  Cocher si obligatoire
                </label>
              </div>

              {needsOptions && (
                <div className="options-section">
                  <h5>Options du champ</h5>

                  {currentField.options.length > 0 && (
                    <div className="options-list">
                      {currentField.options.map((option, idx) => (
                        <div key={idx} className="option-item">
                          <span>
                            {option.label} ({option.value})
                          </span>
                          <button
                            type="button"
                            className="remove-option-btn"
                            onClick={() => handleRemoveOption(idx)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="add-option-form">
                    <div className="form-row">
                      <div className="form-group">
                        <input
                          type="text"
                          value={currentOption.label}
                          onChange={(e) =>
                            setCurrentOption({
                              ...currentOption,
                              label: e.target.value,
                            })
                          }
                          placeholder="Libellé de l'option"
                        />
                      </div>
                      <div className="form-group">
                        <input
                          type="text"
                          value={currentOption.value}
                          onChange={(e) =>
                            setCurrentOption({
                              ...currentOption,
                              value: e.target.value,
                            })
                          }
                          placeholder="Valeur de l'option"
                        />
                      </div>
                      <button
                        type="button"
                        className="add-option-btn"
                        onClick={handleAddOption}
                      >
                        Ajouter l'option
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="add-field-btn"
                onClick={handleAddField}
              >
                Ajouter le champ
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={() => setShowPopup(false)}
          >
            Annuler
          </button>
          <button type="submit" className="save-btn" disabled={loading}>
            {loading ? "Création..." : "Créer le formulaire"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FormCreation;
